import React, { useState, useRef } from "react";
import send from "assets/img/Vector2.png";
import paperclipIcon from "assets/img/paper-clip.svg";
import { getBase64 } from "helpers/file-helper";
import { chatImageMaxFileSize } from "config/constant";
import crossIcon from "assets/img/cross.svg";

type MessageInputProps = {
  handleSendMessage: (inputValue?: string, inputFile?: string) => void;
  active: boolean;
};

const MessageInput = (props: MessageInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState({
    fileName: "",
    fileData: "",
    error: ""
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInputValue(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleInputClick = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleFileClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileSelected = event.target.files?.[0];
    if (fileSelected) {
      const fileBase64: any = await getBase64(fileSelected);
      setSelectedFile((prevState) => ({
        ...prevState,
        fileName: fileSelected.name,
        fileData: fileBase64
      }));
      const fileSizeInKB = fileSelected.size / 1024;
      if (fileSizeInKB > chatImageMaxFileSize) {
        setSelectedFile((prevState) => ({
          ...prevState,
          error: `Max file size allowed is ${chatImageMaxFileSize / 1000} MB`
        }));
      }
      if (!/\.png$|\.jpg$/i.test(fileSelected.name.toLowerCase())) {
        setSelectedFile((prevState) => ({
          ...prevState,
          error: "Only .jpg or .png formats are allowed"
        }));
      }
    }
  };

  const clearFileInput = () => {
    fileInputRef.current.value = "";
    setSelectedFile({
      fileName: "",
      fileData: "",
      error: ""
    });
  };

  const handleSubmit = () => {
    if (props.active && navigator.onLine && !selectedFile.error && inputValue.trim()) {
      selectedFile.fileData
        ? props.handleSendMessage(inputValue, selectedFile.fileData)
        : props.handleSendMessage(inputValue);
      setInputValue("");
      clearFileInput();
    }
  };

  return (
    <label htmlFor="inputfield" aria-label="Please enter the text to chat with proctor">
      <div className="tvp-chatInputfolder tvp-flex tvp-gap-2">
        <div className="tvp-relative tvp-w-full">
          <input
            id="inputfield"
            ref={inputRef}
            className={`tvp-chatInput tvp-w-full ${props.active ? "" : "tvp-disabled"}`}
            value={inputValue}
            name="inputfield"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={!props.active}
            onClick={handleInputClick}
            aria-label="Message input"
          />
          {selectedFile.fileName && (
            <div className="tvp-absolute -tvp-top-6 tvp-bg-gray-light tvp-px-2 tvp-rounded-md tvp-shadow-md tvp-flex tvp-items-center tvp-gap-2">
              <p
                title={selectedFile.fileName}
                className="tvp-text-xs tvp-w-20 tvp-overflow-hidden tvp-whitespace-nowrap tvp-text-ellipsis"
              >
                {selectedFile.fileName}
              </p>
              <button
                data-testid="remove-file-button"
                onClick={clearFileInput}
                className="tvp-border-none"
                aria-label="Remove file"
              >
                <img width={16} height={16} src={crossIcon} alt="upload file icon" />
              </button>
            </div>
          )}
          <input
            onChange={handleFileUpload}
            ref={fileInputRef}
            id="fileid"
            data-testid="file-input"
            accept=".jpg, .png"
            type="file"
            hidden
          />
          {!selectedFile.fileData && (
            <button
              data-testid="file-input-btn"
              onClick={handleFileClick}
              className="tvp-absolute tvp-right-2 tvp-border-none tvp-top-1/2 -tvp-bottom-1/2 -tvp-translate-y-1/2"
              aria-label="Attach file"
            >
              <img width={16} height={16} src={paperclipIcon} alt="close file icon" />
            </button>
          )}
          <div className="tvp-absolute tvp-w-full tvp-right-1/2 tvp-left-1/2 -tvp-translate-x-1/2 tvp-text-orange tvp-text-sm tvp-text-center">
            {selectedFile.error}
          </div>
        </div>
        <div
          className={
            props.active && navigator.onLine && !selectedFile.error && inputValue.trim()
              ? "tvp-iconHolder"
              : "tvp-iconHolderDisabled"
          }
          onClick={handleSubmit}
          role="button"
          aria-label="Send message"
        >
          <img src={send} alt="send icon" />
        </div>
      </div>
    </label>
  );
};

export default MessageInput;
